import { useState, useImperativeHandle, forwardRef, useContext } from "react";
import { useNavigate } from "react-router";

import { ApiContext } from "../../api-context";
import { Modal } from "../../components/modal";

import type { FormMode, ForwardFormRef } from "./shared";
import type {
  CreateTokenSuccessResponse,
  ServiceUser,
} from "@sperrdatei/dto/types";
import type { ForwardedRef, JSX, RefObject } from "react";

type AddUserFormProps =
  | { mode: "INSERT"; domain: string | undefined }
  | { mode: "EDIT"; domain: string | undefined; selectedUser: ServiceUser };

const AddServiceUserForm = (
  props: AddUserFormProps,
  ref: ForwardedRef<{
    validate: () => boolean;
    getResult: () => ServiceUser;
    getMode: () => FormMode;
  }>,
): JSX.Element => {
  const { apiFetch } = useContext(ApiContext);
  const navigate = useNavigate();

  const [validated, setValidated] = useState(false);
  const [usernameError, setUsernameError] = useState<string | undefined>();
  const [user, setUser] = useState<ServiceUser>(
    props.mode === "INSERT" ? { username: "", domain: "" } : props.selectedUser,
  );
  const [isCreateAccessTokenEnabled] = useState(user && user.userId);
  const [token, setToken] = useState<CreateTokenSuccessResponse | undefined>();
  const [tokenCopied, setTokenCopied] = useState(false);

  useImperativeHandle(ref, () => ({
    getResult: (): ServiceUser => user,
    getMode: (): FormMode => props.mode,
    setError: (error: string): void => {
      setValidated(true);
      setUsernameError(error);
    },
    validate: (): boolean => {
      setValidated(true);
      if (!user.username) {
        setUsernameError("Angabe notwendig");
        return false;
      }
      return true;
    },
  }));

  const getAccessToken = async (): Promise<void> => {
    setTokenCopied(false);
    const response = await apiFetch(`/api/user/token/${user.userId}`, {
      method: "POST",
    });

    if (response.ok) {
      setToken(await response.json());
      return;
    }

    navigate("/error");
  };

  if (!props.domain) {
    return (
      <div className="mb-3">
        Es muss zuerst eine Domain angelegt werden, bevor technische Benutzer
        eingerichtet werden können.
      </div>
    );
  }

  return (
    <>
      <div className="mb-3">
        <label htmlFor="username" className="form-text">
          Benutzername / E-Mail
        </label>
        <div className="input-group has-validation">
          <input
            type="text"
            id="username"
            value={user.username}
            disabled={props.mode !== "INSERT"}
            className={
              usernameError
                ? `form-control ${validated ? "is-invalid" : ""}`
                : `form-control ${validated ? "is-valid" : ""}`
            }
            onChange={(e) => {
              if (props.domain) {
                setUser({
                  ...user,
                  username: e.target.value,
                  domain: props.domain,
                });
              }
            }}
            required
            autoFocus
          />
          <button
            className="btn btn-outline-secondary dropdown-toggle domain-toggle"
            type="button"
            disabled={props.mode !== "INSERT"}
          >
            @{props.domain}
          </button>
          <div className="invalid-feedback">
            {usernameError ? usernameError : ""}
          </div>
        </div>
        <div className="mt-3 form-text">
          {!isCreateAccessTokenEnabled && (
            <>
              <span>
                <strong>Hinweis:</strong>
              </span>
              <br />
              Access Tokens können nur für existierende Nutzer erstellt werden.
              Speichern Sie den Benutzer/das Institut und öffnen Sie diese
              Ansicht erneut um einen Access Token zu erstellen.
            </>
          )}
          {!token ? (
            <div>
              <button
                type="button"
                className="btn btn-dark btn-sm mt-3"
                disabled={!isCreateAccessTokenEnabled}
                onClick={async () => {
                  getAccessToken();
                }}
              >
                Access Token Erstellen
              </button>
            </div>
          ) : (
            <>
              <div className="mt-3 p-3 border rounded">
                <div className="mb-2">
                  <strong>Token:</strong>
                  <div className="d-flex align-items-center gap-2 mt-1">
                    <code className="flex-grow-1">{token.token}</code>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => {
                        navigator.clipboard.writeText(token.token);
                        setTokenCopied(true);
                      }}
                      title="Token kopieren"
                    >
                      {tokenCopied ? "✅" : "📋"}
                    </button>
                  </div>
                </div>
                <div className="mb-2">
                  <strong>Ablaufdatum:</strong>
                  <div className="mt-1">
                    {new Date(token.expirationDate).toLocaleString()}
                  </div>
                </div>
                <div>
                  <strong>Hinweis:</strong>
                  <div className="mt-1">
                    Sie müssen den Token speichern oder notieren, da der Token
                    nicht wieder angezeigt werden kann, sobald das Fenster
                    geschlossen wurde.
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

const AddServiceUserModalContent = forwardRef(AddServiceUserForm);

export type ServiceUserModalProps = {
  isAddUserModalOpen: boolean;
  setIsAddUserModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  domain: string | undefined;
  selectedUser: ServiceUser | undefined;
  setSelectedUser: (user: ServiceUser | undefined) => void;
  userFormRef: RefObject<ForwardFormRef<ServiceUser> | null>;
  handleAddUserSubmit: () => void;
};

export const ServiceUserModal: React.FC<ServiceUserModalProps> = ({
  isAddUserModalOpen,
  setIsAddUserModalOpen,
  domain,
  selectedUser,
  setSelectedUser,
  userFormRef,
  handleAddUserSubmit,
}: ServiceUserModalProps) => {
  return (
    <Modal
      className="modal-lg"
      isOpen={isAddUserModalOpen}
      onClose={() => setIsAddUserModalOpen(false)}
      title="Technische Benutzer"
      primaryAction={{
        label: "OK",
        onClick: (e) => {
          e.preventDefault();
          handleAddUserSubmit();
          setSelectedUser(undefined);
        },
      }}
      secondaryAction={{
        label: "Abbrechen",
        onClick: () => {
          setIsAddUserModalOpen(false);
          setSelectedUser(undefined);
        },
      }}
    >
      <AddServiceUserModalContent
        ref={userFormRef}
        {...(selectedUser
          ? { mode: "EDIT", selectedUser, domain }
          : { mode: "INSERT", domain })}
      />
    </Modal>
  );
};
